import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle } from "lucide-react";
import { Btn, Field, Input, Select, TextArea } from "./ui";
import { UploadField } from "./UploadField";
import { ProvLoc } from "./ProvLoc";
import { errMsg, todayLocal as today } from "./api";

const NAZAR = "¿Conocés a Claudio Nazar?";

// Mobile-first form for collaborators to send their own material.
export const EnvioForm = ({ client, meta, user, onSent }) => {
  const init = { nombre: user?.nombre || "", provincia: user?.provincia || "", localidad: user?.localidad || "", fecha_grabacion: today(), lugar: "", categoria: "", titulo: "", descripcion: "", autorizacion: false };
  const [f, setF] = useState(init);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [k, setK] = useState(0);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Subí el archivo primero");
    if (!f.autorizacion) return toast.error("Tenés que aceptar la declaración de autorización");
    setBusy(true);
    try {
      await client.post("/envios", { ...f, file_id: file.file_id });
      setSent(true);
      onSent?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white border border-gray-200 p-8 text-center" data-testid="envio-enviado">
        <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
        <h3 className="font-headings uppercase text-xl text-[#0A192F]">¡Recibimos tu material!</h3>
        <p className="text-sm text-gray-600 mt-2">Queda privado hasta que el equipo de moderación lo revise. Te avisamos por email y en “Mis envíos”.</p>
        <Btn className="mt-5" onClick={() => { setSent(false); setF(init); setFile(null); setK((x) => x + 1); }} data-testid="envio-otro">Enviar otro</Btn>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 p-4 sm:p-6 grid sm:grid-cols-2 gap-4" key={k} data-testid="envio-form">
      {f.categoria === NAZAR && (
        <div className="sm:col-span-2 bg-[#0A192F] text-white p-4 flex gap-3">
          <HelpCircle className="text-[#F5B400] shrink-0" />
          <p className="text-sm">Preguntá en la calle: <strong>¿Conocés a Claudio Nazar?</strong> y <strong>¿qué le preguntarías?</strong> Pedí permiso antes de grabar.</p>
        </div>
      )}
      <Field label="Archivo (video o foto) *" className="sm:col-span-2">
        <UploadField client={client} onUploaded={setFile} testid="envio-archivo" />
      </Field>
      <Field label="Categoría *" className="sm:col-span-2">
        <Select required value={f.categoria} placeholder="Elegí la categoría…" options={meta.categorias} onChange={(e) => set({ categoria: e.target.value })} data-testid="envio-categoria" />
      </Field>
      <Field label="Título *" className="sm:col-span-2"><Input required minLength={3} value={f.titulo} onChange={(e) => set({ titulo: e.target.value })} placeholder="Ej: Entrevistas en la plaza central" data-testid="envio-titulo" /></Field>
      <Field label="Tu nombre *" className="sm:col-span-2"><Input required minLength={2} value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} data-testid="envio-nombre" /></Field>
      <ProvLoc meta={meta} provincia={f.provincia} localidad={f.localidad} onChange={set} testid="envio" />
      <Field label="Fecha de grabación *"><Input required type="date" max={today()} value={f.fecha_grabacion} onChange={(e) => set({ fecha_grabacion: e.target.value })} data-testid="envio-fecha" /></Field>
      <Field label="Lugar de grabación *"><Input required minLength={2} value={f.lugar} onChange={(e) => set({ lugar: e.target.value })} placeholder="Ej: Plaza San Martín" data-testid="envio-lugar" /></Field>
      <Field label="Descripción * (mín. 10 caracteres)" className="sm:col-span-2"><TextArea required minLength={10} value={f.descripcion} onChange={(e) => set({ descripcion: e.target.value })} placeholder="¿Qué se ve? ¿Quiénes participan?" data-testid="envio-descripcion" /></Field>
      <label className="sm:col-span-2 flex items-start gap-3 p-3 border border-amber-300 bg-amber-50 cursor-pointer">
        <input type="checkbox" checked={f.autorizacion} onChange={(e) => set({ autorizacion: e.target.checked })} className="mt-1 w-5 h-5 accent-[#0A2463] shrink-0" data-testid="envio-autorizacion" />
        <span className="text-sm text-[#0A192F]">
          <strong>Declaro</strong> que el material es mío o tengo permiso para compartirlo, que las personas que aparecen autorizaron ser grabadas y su publicación, y autorizo a Frente Ya a publicarlo y difundirlo.
        </span>
      </label>
      <div className="sm:col-span-2">
        <Btn type="submit" variant="gold" busy={busy} disabled={!file} className="w-full sm:w-auto !py-3.5" data-testid="envio-submit">Enviar para revisión</Btn>
      </div>
    </form>
  );
};

export default EnvioForm;
